import { computed, ref, watch, watchEffect, type ComputedRef, type Ref } from 'vue';

export type ColorModePreference = 'light' | 'dark' | 'auto';
export type ResolvedColorMode = Exclude<ColorModePreference, 'auto'>;

export const COLOR_MODE_STORAGE_KEY = 'colorMode';

const PREFERENCES: ColorModePreference[] = ['light', 'dark', 'auto'];
const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

interface ColorModeState {
  preference: Ref<ColorModePreference>;
  resolved: ComputedRef<ResolvedColorMode>;
}

let state: ColorModeState | null = null;

function isPreference(value: string | null): value is ColorModePreference {
  return PREFERENCES.includes(value as ColorModePreference);
}

function readStoredPreference(): ColorModePreference {
  try {
    const stored = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
    return isPreference(stored) ? stored : 'light';
  } catch {
    return 'light';
  }
}

function createState(): ColorModeState {
  const preference = ref<ColorModePreference>(readStoredPreference());
  const systemPrefersDark = ref(false);

  const media = window.matchMedia?.(DARK_MEDIA_QUERY);
  if (media) {
    systemPrefersDark.value = media.matches;
    media.addEventListener('change', (event) => {
      systemPrefersDark.value = event.matches;
    });
  }

  const resolved = computed<ResolvedColorMode>(() => {
    if (preference.value !== 'auto') {
      return preference.value;
    }
    return systemPrefersDark.value ? 'dark' : 'light';
  });

  // The class goes on <html>, not on #app: modals, tooltips and selects teleport to <body>.
  watchEffect(() => {
    const root = document.documentElement.classList;
    root.toggle('dark', resolved.value === 'dark');
    root.toggle('light', resolved.value === 'light');
  });

  watch(preference, (value) => {
    try {
      localStorage.setItem(COLOR_MODE_STORAGE_KEY, value);
    } catch {
      // Persisting is best effort; the choice still applies for this session.
    }
  });

  return { preference, resolved };
}

export function useAppColorMode() {
  state ??= createState();
  const { preference, resolved } = state;

  const cycle = () => {
    const index = PREFERENCES.indexOf(preference.value);
    preference.value = PREFERENCES[(index + 1) % PREFERENCES.length];
  };

  return { preference, resolved, cycle };
}
